import {
    Address,
    CheckoutSelectors,
    Country,
    Customer,
    FormField,
} from '@bigcommerce/checkout-sdk';
import { FormikProps, withFormik } from 'formik';
import React, { RefObject ,useRef, useState } from 'react';
import { connect } from 'react-redux'
import { lazy, object } from 'yup';

import { TranslatedString, withLanguage, WithLanguageProps } from '@bigcommerce/checkout/locale';
import { usePayPalFastlaneAddress } from '@bigcommerce/checkout/paypal-fastlane-integration';
import { AddressFormSkeleton } from '@bigcommerce/checkout/ui';

import {
    AddressForm,
    AddressFormValues,
    AddressSelect,
    AddressType,
    getAddressFormFieldsValidationSchema,
    getTranslateAddressError,
    isValidCustomerAddress,
    mapAddressToFormValues,
} from '../address';
import B3Checkout from '../b3checkout'
import CheckoutStepType from '../checkout/CheckoutStepType';
import { getCustomFormFieldsValidationSchema } from '../formFields';
import { OrderComments } from '../orderComments';
import { Button, ButtonVariant } from '../ui/button';
import { Fieldset, Form } from '../ui/form';
import { LoadingOverlay } from '../ui/loading';

import StaticBillingAddress from './StaticBillingAddress'

export type BillingFormValues = AddressFormValues & { orderComment: string } & { b2bCustomFields?: any }

export interface BillingFormProps {
    billingAddress?: Address
    countries: Country[]
    countriesWithAutocomplete: string[]
    customer: Customer
    customerMessage: string
    googleMapsApiKey: string
    isUpdating: boolean
    methodId?: string
    shouldShowOrderComments: boolean
    isFloatingLabelEnabled?: boolean
    getFields(countryCode?: string): FormField[]
    onSubmit(values: BillingFormValues): void
    onUnhandledError(error: Error): void
    updateAddress(address: Partial<Address>): Promise<CheckoutSelectors>
    type: CheckoutStepType
}

interface B3Props {
    b3Company: any
    isB3User: boolean
    checkoutSettings: any
}

const mapStateToProps = (state: { global: { isB3User: boolean, checkoutSettings: any }, company: any }) => {
    const {
        global: {
            isB3User,
            checkoutSettings,
        },
        company: b3Company
    } = state

    return {
        b3Company,
        isB3User,
        checkoutSettings,
    }
}


const BillingForm = ({
    googleMapsApiKey,
    billingAddress,
    countriesWithAutocomplete,
    customer: { addresses, isGuest },
    getFields,
    countries,
    isUpdating,
    setFieldValue,
    shouldShowOrderComments,
    values,
    methodId,
    isFloatingLabelEnabled,
    updateAddress,
    onUnhandledError,
    b3Company,
    isB3User,
    checkoutSettings,
    type,
    setValues,
}: B3Props & BillingFormProps & WithLanguageProps & FormikProps<BillingFormValues>) => {
    const [isResettingAddress, setIsResettingAddress] = useState(false);
    const addressFormRef: RefObject<HTMLFieldSetElement> = useRef(null);
    const { isPayPalFastlaneEnabled, mergedBcAndPayPalFastlaneAddresses } = usePayPalFastlaneAddress();

    const shouldShowSaveAddress = () => {
        const {
            storage,
            constants,
        } = B3Checkout.utils

        const B3RoleId = storage.B3RoleId.getValue()

        const { ADMIN, SENIOR } = constants.B3Role
    
        return isB3User
            ? [ADMIN, SENIOR].includes(B3RoleId)
            : !isGuest
    }

    const shouldShowSaveAddressInCheckoutSettings = () => {
        return checkoutSettings['billing.address.save']
    }

    const b2bAddresses = () => {

        return isB3User
            ? b3Company.addresses.filter((address: { isBilling: number }) => address.isBilling === 1)
            : addresses
    }

    const validateAddress = () => {

        const formFields = getFields(billingAddress?.countryCode)

        const allAdresses = b2bAddresses()

        const isValidateExtrasField = B3Checkout.extrasFiles.isValidExtrasFiles('billing')

        return isValidateExtrasField && B3Checkout.utils.util.validateAddress(billingAddress, allAdresses, formFields)
    }

    const shouldShowEnterNewAddress = () => {

        return checkoutSettings['billing.address.add']
    }

    const shouldRenderStaticAddress = methodId === 'amazonpay';
    const allFormFields = getFields(values.countryCode);
    const customFormFields = allFormFields.filter(({ custom }) => custom);
    const hasCustomFormFields = customFormFields.length > 0;
    const editableFormFields =
        shouldRenderStaticAddress && hasCustomFormFields ? customFormFields : allFormFields;
    const billingAddresses = isPayPalFastlaneEnabled ? mergedBcAndPayPalFastlaneAddresses : b2bAddresses();
    const hasAddresses = billingAddresses?.length > 0;
    const bcValidCustomerAddress = () => {
        return billingAddress &&
        isValidCustomerAddress(
            billingAddress,
            billingAddresses,
            getFields(billingAddress.countryCode),
        )
    }
    const hasValidCustomerAddress = isB3User ?  validateAddress() : bcValidCustomerAddress()
        

    const handleSelectAddress = async (address: Partial<Address>) => {
        setIsResettingAddress(true);

        try {
            if (isB3User) B3Checkout.utils.storage.selectBillAddressId.setValue((address as any)?.addressId)

            if (B3Checkout.extrasFiles.getB3BillingAddressExtras()?.length) {
                const isEditAddress = B3Checkout.extrasFiles.setAddressExtrasFiles(isB3User, address, false)
                const getB2bCustomFields = isB3User ? B3Checkout.extrasFiles.setAddressSelectExtrasFiles(address) : {}
                const addressBilling: any = { ...values }

                Object.keys(addressBilling).map((item: any) => {
                    if (item !== 'orderComment') {
                        addressBilling[item] = (address as any)[item] || ''
                    }
                })
                if (!isEditAddress) await updateAddress(B3Checkout.utils.address.addressFieldOptimize(address))
                setValues({
                    ...addressBilling,
                    addressId: '',
                    b2bCustomFields: getB2bCustomFields
                } as BillingFormValues)

                return
            }
 
                await updateAddress(B3Checkout.utils.address.addressFieldOptimize(address));

                const isValidAddress = isValidCustomerAddress(
                    (address as any),
                    billingAddresses,
                    getFields(address.countryCode),
                )
                const addressBilling: any = { ...values }

                Object.keys(addressBilling).map((item: any) => {
                    if (item !== 'orderComment') {
                        addressBilling[item] = (address as any)[item] || ''
                    }
                })
                if (isValidAddress) setValues({
                    ...addressBilling,
                    addressId: '',
                } as BillingFormValues)
            

   
        } catch (error) {
            if (error instanceof Error) {
                onUnhandledError(error);
            }
        } finally {
            setIsResettingAddress(false);
        }
    };

    const handleUseNewAddress = async () => {
        handleSelectAddress(isB3User ? b3Company.basicAddressInfo : {});
    };

    return (
        <Form autoComplete="on">
            {shouldRenderStaticAddress && billingAddress && (
                <div className="form-fieldset">
                    <StaticBillingAddress address={billingAddress} />
                </div>
            )}

            <Fieldset id="checkoutBillingAddress" ref={addressFormRef}>
                {hasAddresses && !shouldRenderStaticAddress && (
                    <Fieldset id="billingAddresses">
                        <LoadingOverlay isLoading={isResettingAddress}>
                            <AddressSelect
                                addresses={billingAddresses}
                                onSelectAddress={handleSelectAddress}
                                onUseNewAddress={handleUseNewAddress}
                                selectedAddress={
                                    hasValidCustomerAddress ? billingAddress : undefined
                                }
                                type={AddressType.Billing}
                            />
                        </LoadingOverlay>
                    </Fieldset>
                )}

                {
                     shouldShowEnterNewAddress() && (
                        <p>
                            OR <a onClick={ () => handleUseNewAddress() }>{ B3Checkout.locales.tips['b3.addresses.enterNew'] }</a>
                        </p>
                    )
                }


                {!hasValidCustomerAddress && (!isB3User || shouldShowEnterNewAddress()) && (
                    <AddressFormSkeleton isLoading={isResettingAddress}>
                        <AddressForm
                            countries={countries}
                            countriesWithAutocomplete={countriesWithAutocomplete}
                            countryCode={values.countryCode}
                            formFields={editableFormFields}
                            googleMapsApiKey={googleMapsApiKey}
                            isFloatingLabelEnabled={isFloatingLabelEnabled}
                            isLoadingB2bAddress={ true }
                            setFieldValue={setFieldValue}
                            shouldShowSaveAddress={shouldShowSaveAddress() && shouldShowSaveAddressInCheckoutSettings()}
                            type={ type }
                        />
                    </AddressFormSkeleton>
                )}
            </Fieldset>

            {shouldShowOrderComments && <OrderComments />}

            <div className="form-actions">
                <Button
                    disabled={isUpdating || isResettingAddress}
                    id="checkout-billing-continue"
                    isLoading={isUpdating || isResettingAddress}
                    type="submit"
                    variant={ButtonVariant.Primary}
                >
                    <TranslatedString id="common.continue_action" />
                </Button>
            </div>
        </Form>
    );
};



export default connect(mapStateToProps)(withLanguage(withFormik<B3Props & BillingFormProps & WithLanguageProps, BillingFormValues>({
    handleSubmit: (values, { props: { onSubmit } }) => {
        onSubmit(values)
    },
    mapPropsToValues: ({ getFields, customerMessage, billingAddress }) => (
        {
            ...mapAddressToFormValues(
                getFields(billingAddress && billingAddress.countryCode),
                billingAddress,
            ),
            b2bCustomFields: B3Checkout.extrasFiles.setGoBackEcho('billing'),
            orderComment: customerMessage,
        }
    ),
    isInitialValid: ({
        billingAddress,
        getFields,
        language,
    }) => (
        !!billingAddress && getAddressFormFieldsValidationSchema({
            language,
            formFields: getFields(billingAddress.countryCode),
        }).isValidSync(billingAddress)
    ),
    validationSchema: ({
        language,
        getFields,
        methodId,
    }: BillingFormProps & WithLanguageProps) => methodId === 'amazonpay' ?
            (lazy<Partial<AddressFormValues>>(values => getCustomFormFieldsValidationSchema({
                translate: getTranslateAddressError(language),
                formFields: getFields(values && values.countryCode),
            }))) :
            (lazy<Partial<AddressFormValues>>(values => {
                return getAddressFormFieldsValidationSchema({
                    language,
                    formFields: getFields(values && values.countryCode),
                }).concat(object({
                    b2bCustomFields: B3Checkout.extrasFiles.getB3FormFieldsValidationSchema({
                        translate: B3Checkout.extrasFiles.getTranslateAddressError(language),
                        formFields: B3Checkout.extrasFiles.getB3ShippingAddressExtras(),
                    })
                }))
            })),
    enableReinitialize: true,
})(BillingForm)))
